/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { AsyncPipe, DOCUMENT } from "@angular/common";
import { Component, inject, OnInit } from "@angular/core";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { GridOptions } from "ag-grid-community";
import { EChartsType } from "echarts";
import { BehaviorSubject, combineLatest, map, Observable, tap } from "rxjs";
import { ButtonComponent } from "../../../shared/components/button/button.component";
import { ChartComponent } from "../../../shared/components/chart/chart.component";
import { ChipsComponent } from "../../../shared/components/chips/chips.component";
import { DatepickerComponent } from "../../../shared/components/datepicker/datepicker.component";
import { DropdownComponent } from "../../../shared/components/dropdown/dropdown.component";
import { KpiComponent } from "../../../shared/components/kpi/kpi.component";
import { MapComponent } from "../../../shared/components/map/map.component";
import { SpinnerComponent } from "../../../shared/components/spinner/spinner.component";
import { SwitchComponent } from "../../../shared/components/switch/switch.component";
import { TableComponent } from "../../../shared/components/table/table.component";
import { BarChart } from "../../../shared/models/http/charts/bar-chart/bar-chart.model";
import { LineChartSerie } from "../../../shared/models/http/charts/line-chart/line-chart-serie.model";
import { LineChart } from "../../../shared/models/http/charts/line-chart/line-chart.model";
import { MapChart } from "../../../shared/models/http/charts/map-chart/map-chart.model";
import { MapRegion } from "../../../shared/models/http/charts/map-chart/map-region.model";
import { PieChart } from "../../../shared/models/http/charts/pie-chart/pie-chart.model";
import { HardwareAssetsRealtimeInformation } from "../../../shared/models/http/dashboard/hardware-assets-realtime-information.model";
import { HardwareAssetsRealtimeManufacturerTable } from "../../../shared/models/http/dashboard/hardware-assets-realtime-manufacturer-table.model";
import { IctCarbonInsightsReportTab } from "../../../shared/models/http/dashboard/ict-carbon-insights-report.model";
import { TotalMonths } from "../../../shared/models/http/dashboard/hardware-assets-realtime-total-months.model";
import { ChartResponse } from "../../../shared/models/ui/charts.model";
import { ChipOption } from "../../../shared/models/ui/chip-option.model";
import { SelectOption } from "../../../shared/models/ui/select-option.model";
import { TimePeriodRange } from "../../../shared/models/ui/time-period-range.model";
import { MaxDecimalsPipe } from "../../../shared/pipes/max-decimals.pipe";
import { AuthorizationService } from "../../../shared/services/authorization.service";
import { DashboardHardwareAssetsRealtimeService } from "../../../shared/services/dashboard-hardware-assets-realtime.service";
import { IctCarbonInsightsReportService } from "../../../shared/services/ict-carbon-insights-report.service";
import { PATHS } from "../../../shared/utils/constants";
import { GRANTS } from "../../../shared/utils/grants.constants";
import { lazyLoad, Loadable } from "../../../shared/utils/lazy-load";
import { getCurrentWeekRange } from "../../../shared/utils/reporting-period";
import { formatDateForBE, saveAs } from "../../../shared/utils/utils";

type RealtimeFilters = {
	startDate: string;
	endDate?: string;
	country?: string;
	category?: string;
};

@Component({
	selector: "globalictcc-hardware-assets-realtime",
	standalone: true,
	imports: [
		AsyncPipe,
		ButtonComponent,
		ChartComponent,
		ChipsComponent,
		DatepickerComponent,
		DropdownComponent,
		KpiComponent,
		MapComponent,
		SwitchComponent,
		TableComponent,
		SpinnerComponent,
		MaxDecimalsPipe,
		ReactiveFormsModule
	],
	templateUrl: "./hardware-assets-realtime.component.html",
	styleUrl: "./hardware-assets-realtime.component.scss"
})
export class HardwareAssetsRealtimeComponent implements OnInit {
	private readonly _document: Document = inject(DOCUMENT);
	public readonly grants = GRANTS.hwart;
	public readonly authorizationService = inject(AuthorizationService);
	public periodControl = new FormControl<TimePeriodRange>(getCurrentWeekRange());

	public viewSwitchOptions: SelectOption[] = [
		{ label: "Baseline", value: "baseline" },
		{ label: "Realtime", value: "realtime", selected: true }
	];
	public sectionChips: ChipOption[] = [
		{ label: "DEVICE ANALYSIS", value: "device", selected: true },
		{ label: "LOCATIONS", value: "locations" },
		{ label: "EMBODIED EMISSIONS", value: "embodied" }
	];
	public categoriesOptions: SelectOption[] = [{ label: "All", value: "ALL", selected: true }];
	public categoriesControl = new FormControl<string>("ALL");
	public countriesOptions: SelectOption[] = [];
	public countriesControl = new FormControl<string>("");
	public manufacturersTableHasError = false;
	public activeSection: "device" | "locations" | "embodied" = "device";

	private readonly _filters$ = new BehaviorSubject<RealtimeFilters | undefined>(undefined);
	private readonly _loadingWidgets = new Set<string>();

	public manufacturersGridOptions: GridOptions = {
		columnDefs: [
			{ headerName: "Manufacturer name", field: "manufacturer" },
			{ headerName: "Devices count", field: "parts" },
			{ headerName: "tCO2e", field: "emissionValue" }
		],
		rowData: []
	};

	constructor(
		private _router: Router,
		private _dashboardHardwareAssetsRealtimeService: DashboardHardwareAssetsRealtimeService,
		private _ictCarbonInsightsReportService: IctCarbonInsightsReportService
	) {}

	public readonly information$: Observable<Loadable<HardwareAssetsRealtimeInformation>> =
		lazyLoad(this._filters$, filters =>
			this._dashboardHardwareAssetsRealtimeService.getInformations(
				filters.startDate,
				filters.endDate
			)
		).pipe(tap(state => this._trackWidgetLoading("information", state)));

	public readonly totalEmissions$: Observable<Loadable<ChartResponse<LineChart>>> = lazyLoad(
		this._filters$,
		filters =>
			this._dashboardHardwareAssetsRealtimeService
				.getTotalEmissions(filters.startDate, filters.endDate)
				.pipe(map(total => this._buildTotalEmissionsChart(total)))
	).pipe(tap(state => this._trackWidgetLoading("totalEmissions", state)));

	public readonly modelImpact$: Observable<
		Loadable<[ChartResponse<BarChart>, ChartResponse<BarChart>]>
	> = lazyLoad(this._filters$, filters =>
		combineLatest([
			this._dashboardHardwareAssetsRealtimeService.getEmissionsByModelTotal(
				filters.startDate,
				filters.endDate
			),
			this._dashboardHardwareAssetsRealtimeService.getEmissionsByModelPerDevice(
				filters.startDate,
				filters.endDate
			)
		])
	).pipe(tap(state => this._trackWidgetLoading("modelImpact", state)));

	public readonly locationEmissions$: Observable<Loadable<ChartResponse<BarChart>>> = lazyLoad(
		this._filters$,
		filters =>
			this._dashboardHardwareAssetsRealtimeService.getEmissionsByLocation(filters.country)
	).pipe(tap(state => this._trackWidgetLoading("locationEmissions", state)));

	public readonly geographicAnalysis$: Observable<Loadable<ChartResponse<MapChart>>> = lazyLoad(
		this._filters$,
		() =>
			this._dashboardHardwareAssetsRealtimeService.getCarbonIntensityMap().pipe(
				tap(mapChart => {
					this.countriesOptions = this._buildCountriesOptions(
						mapChart.chart?.mapRegions || []
					);
					if (!this.countriesControl.value && this.countriesOptions.length) {
						this.countriesControl.setValue(this.countriesOptions[0].value.toString());
					}
				})
			)
	).pipe(tap(state => this._trackWidgetLoading("geographicAnalysis", state)));

	public readonly manufacturerData$: Observable<
		Loadable<[HardwareAssetsRealtimeManufacturerTable, ChartResponse<PieChart>]>
	> = lazyLoad(this._filters$, filters =>
		combineLatest([
			this._dashboardHardwareAssetsRealtimeService.getManufacturersEmission(filters.category),
			this._dashboardHardwareAssetsRealtimeService.getManufacturerPieChart(filters.category)
		]).pipe(
			tap(([table]) => {
				this.manufacturersGridOptions = {
					...this.manufacturersGridOptions,
					rowData: table.manufacturersEmissions
				};
				this.manufacturersTableHasError = false;
			})
		)
	).pipe(tap(state => this._trackWidgetLoading("manufacturerData", state)));

	public readonly locationTimeSeries$: Observable<Loadable<ChartResponse<LineChart>>> = lazyLoad(
		this._filters$,
		() => this._dashboardHardwareAssetsRealtimeService.getEnergyConsumption()
	).pipe(tap(state => this._trackWidgetLoading("locationTimeSeries", state)));

	public readonly savedLifecycleEmissions$: Observable<Loadable<ChartResponse<LineChart>>> =
		lazyLoad(this._filters$, () =>
			this._dashboardHardwareAssetsRealtimeService.getEmbodiedEmissions()
		).pipe(tap(state => this._trackWidgetLoading("savedLifecycleEmissions", state)));

	ngOnInit(): void {
		this._applyFilters();
	}

	public onViewSwitch(targetView: string): void {
		if (targetView === "baseline") {
			this._router.navigateByUrl(`${PATHS.DASHBOARD}/${PATHS.HARDWARE_ASSETS}`);
		}
	}

	public onApply(): void {
		this._applyFilters();
	}

	public onCountryChange(selected: string | string[] | number): void {
		const value = Array.isArray(selected) ? selected[0] : selected;
		this.countriesControl.setValue(value?.toString() ?? "");
		this._applyFilters();
	}

	public onSectionChange(selected: string | number): void {
		this.sectionChips = this.sectionChips.map(chip => ({
			...chip,
			selected: chip.value === selected
		}));
		this.activeSection = selected as "device" | "locations" | "embodied";
	}

	public getVisibleSectionChips(
		canSeeDevice: boolean,
		canSeeLocations: boolean,
		canSeeEmbodied: boolean
	): ChipOption[] {
		const visibleSections = new Set<string>();
		if (canSeeDevice) visibleSections.add("device");
		if (canSeeLocations) visibleSections.add("locations");
		if (canSeeEmbodied) visibleSections.add("embodied");
		return this.sectionChips.filter(chip => visibleSections.has(chip.value.toString()));
	}

	public getActiveSection(
		canSeeDevice: boolean,
		canSeeLocations: boolean,
		canSeeEmbodied: boolean
	): "device" | "locations" | "embodied" | undefined {
		const visibleSections = this.getVisibleSectionChips(
			canSeeDevice,
			canSeeLocations,
			canSeeEmbodied
		).map(chip => chip.value.toString());

		if (visibleSections.includes(this.activeSection)) return this.activeSection;
		return visibleSections[0] as "device" | "locations" | "embodied" | undefined;
	}

	public onExportChart(chartInstance?: EChartsType, imageName?: string) {
		if (!chartInstance) return;

		const originalOption = chartInstance.getOption();
		const optionWithoutDataZoom = {
			...originalOption,
			dataZoom: []
		};

		chartInstance.setOption(optionWithoutDataZoom, { notMerge: true });

		setTimeout(() => {
			const chartAsPngUrl = chartInstance.getDataURL({ type: "png" });
			saveAs(this._document, chartAsPngUrl, imageName || "chart");
			chartInstance.setOption(originalOption, { notMerge: true });
		}, 300);
	}

	private _applyFilters(): void {
		const formOptions = this._getFormOptions();
		if (!formOptions) return;

		this._filters$.next({
			...formOptions,
			country: this.countriesControl.value || undefined,
			category: this.categoriesControl.value || undefined
		});
	}

	private _trackWidgetLoading<T>(key: string, state: Loadable<T>) {
		queueMicrotask(() => {
			if (state.isLoading) this._loadingWidgets.add(key);
			else this._loadingWidgets.delete(key);
		});
	}

	public isActiveSectionLoading(
		section: "device" | "locations" | "embodied" | undefined
	): boolean {
		if (section === "device") {
			return (
				this._loadingWidgets.has("modelImpact") ||
				this._loadingWidgets.has("manufacturerData")
			);
		}
		if (section === "locations") {
			return (
				this._loadingWidgets.has("geographicAnalysis") ||
				this._loadingWidgets.has("locationEmissions") ||
				this._loadingWidgets.has("locationTimeSeries")
			);
		}
		return section === "embodied" && this._loadingWidgets.has("savedLifecycleEmissions");
	}

	private _buildTotalEmissionsChart(totalEmissions: TotalMonths): ChartResponse<LineChart> {
		const months = [...(totalEmissions?.months || [])].sort((firstMonth, secondMonth) =>
			firstMonth.month.localeCompare(secondMonth.month)
		);
		if (!months.length) return { chart: { lineSeries: [] }, hasError: true };

		const unit = months[0].unit;
		const lineSeries: LineChartSerie[] = [
			{
				name: "Scope 2",
				linePoints: months.map(point => ({ name: point.month, value: point.scope2, unit }))
			},
			{
				name: "Scope 3",
				linePoints: months.map(point => ({ name: point.month, value: point.scope3, unit }))
			}
		];

		return { chart: { title: "Total emissions over time", lineSeries }, hasError: false };
	}

	private _buildCountriesOptions(mapRegions: MapRegion[]): SelectOption[] {
		return mapRegions.map(region => ({ label: region.name, value: region.shortName }));
	}

	private _getFormOptions(): { startDate: string; endDate?: string } | undefined {
		const timePeriod = this.periodControl.value || getCurrentWeekRange();
		if (!timePeriod?.start) return undefined;

		return {
			startDate: formatDateForBE(timePeriod.start)!,
			endDate: formatDateForBE(timePeriod.end)
		};
	}

	public onExportReport(): void {
		const formOptions = this._getFormOptions();
		if (!formOptions?.endDate) return;

		this._ictCarbonInsightsReportService
			.downloadReport(IctCarbonInsightsReportTab.HARDWARE, {
				from: formOptions.startDate,
				to: formOptions.endDate
			})
			.subscribe();
	}
}
